import { AsyncPipe, DecimalPipe, DatePipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { PageToolbarComponent, NotificationEmptyComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors, SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { CowDetailsDialogComponent } from 'src/app/shared/components/cow-details-dialog/cow-details-dialog.component';
import { Production } from 'src/app/shared/models/production.model';

import { ProductionPickerComponent } from '../../../shared/components/production-picker/production-picker.component';
import { Cow } from '../../models/fertility.model';
import * as FertilityActions from '../../store/actions/fertility-actions';
import { getCows } from '../../store/selectors/fertility-selectors';

@Component({
  selector: 'app-individual-fertility',
  templateUrl: './individual-fertility.component.html',
  styleUrls: ['./individual-fertility.component.scss'],
  imports: [
    PageToolbarComponent,
    ProductionPickerComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatButton,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NotificationEmptyComponent,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class IndividualFertilityComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);
  private readonly dialogService = inject(DialogService);

  @ViewChild(MatSort) sort: MatSort;

  public pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  public columns: {
    id: string;
    label: string;
    subLabel?: string;
    type:
      | 'string'
      | 'number'
      | 'date'
      | 'link'
      | 'insemination'
      | 'pregnancy-status'
      | 'expected-calving-date';
  }[] = [
    {
      id: 'number',
      label: 'fertility.individual-fertility.number',
      type: 'number',
    },
    {
      id: 'name',
      label: 'fertility.individual-fertility.name',
      type: 'link',
    },
    {
      id: 'internationalCowId',
      label: 'fertility.individual-fertility.internationalCowId',
      type: 'string',
    },
    {
      id: 'parity',
      label: 'fertility.individual-fertility.parity',
      type: 'number',
    },
    {
      id: 'lactationDays',
      label: 'fertility.individual-fertility.lactationDays',
      type: 'number',
    },
    {
      id: 'pregnancyStatus',
      label: 'fertility.individual-fertility.pregnancyStatus',
      type: 'pregnancy-status',
    },
    {
      id: 'daysPregnant',
      label: 'fertility.individual-fertility.daysPregnant',
      type: 'number',
    },
    {
      id: 'lastCalvingDate',
      label: 'fertility.individual-fertility.lastCalvingDate',
      type: 'date',
    },
    {
      id: 'intercalvingDays',
      label: 'fertility.individual-fertility.intercalvingDays',
      type: 'number',
    },
    {
      id: 'lastInseminationDate',
      label: 'fertility.individual-fertility.lastInseminationDate',
      type: 'date',
    },
    {
      id: 'expectedIntercalvingDays',
      label: 'fertility.individual-fertility.expectedIntercalvingDays',
      type: 'number',
    },
    {
      id: 'expectedCalvingDate',
      label: 'fertility.individual-fertility.expectedCalvingDate',
      type: 'expected-calving-date',
    },
    {
      id: 'daysOpen',
      label: 'fertility.individual-fertility.daysOpen',
      type: 'number',
    },
    {
      id: 'numberOfInseminationsAfterCalving',
      label: 'fertility.individual-fertility.numberOfInseminationsAfterCalving',
      type: 'insemination',
    },
  ];
  public dateFormat: string;
  public dataSource = new MatTableDataSource<Cow>();
  public hasData = false;

  private getCows$ = this.store.select(getCows);
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.store.dispatch(
      SiteActions.updatePageTitle({ pageTitle: 'fertility.individual-fertility.page-title' }),
    );
    this.dateFormat = this.localeService.translate('common.date-format') ?? 'dd-MM-yyyy';
  }

  ngAfterViewInit() {
    console.log('GetCows set sort afterview');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onProductionChanged(production: Production): void {
    this.store.dispatch(
      FertilityActions.loadCowsByDateRequest({
        date: new Date(production.date).toISOString(),
      }),
    );
    this.getCows$
      .pipe(
        filter((result) => !!result),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        this.dataSource.data = result;
        this.dataSource.sort = this.sort;
        console.log('GetCows after return');
        this.hasData = true;
      });
  }

  public columnIds = (): string[] => this.columns.map((item) => item.id);

  public openCowDetails({ internationalCowId }: Cow): void {
    this.dialogService
      .fullscreenDialog(CowDetailsDialogComponent, {
        internationalCowId,
        title: this.localeService.translate('dairyresults.cow-details.title'),
        setPendingChanges: false,
        fullscreen: true,
      })
      .subscribe(({ status }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          // Handle the confirmation status but do nothing
        }
      });
  }
}
